const WIDTH = 100;
const HEIGHT = 28;
const PADDING = 2;

const round = (value) => Math.round(value * 100) / 100;

/**
 * A trend line with no axes, for "is this going down, and how fast".
 *
 * Inline SVG rather than chart.js: no canvas (so nothing to mock in tests), no
 * registration, and `currentColor` makes it follow the text colour in both
 * themes. `preserveAspectRatio="none"` stretches it to whatever box it is
 * given; the non-scaling stroke keeps the line weight constant while it does.
 *
 * Non-numeric points are skipped. Fewer than two numbers is not a trend, so
 * nothing is rendered and the caller decides what to show instead.
 *
 * A screen reader cannot see the shape, so with `formatValue` the accessible
 * name also says where the line starts and ends ("…, from $20.00 to $4.50").
 */
export default function Sparkline({ points, ariaLabel, formatValue, className = 'h-8 w-full' }) {
  const values = (Array.isArray(points) ? points : []).filter(
    (value) => typeof value === 'number' && Number.isFinite(value),
  );
  if (values.length < 2) return null;
  const label = typeof formatValue === 'function'
    ? `${ariaLabel}, from ${formatValue(values[0])} to ${formatValue(values[values.length - 1])}`
    : ariaLabel;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const line = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * WIDTH;
      // A flat series has no range to scale into; draw it through the middle.
      const y = span === 0 ? HEIGHT / 2 : PADDING + (1 - (value - min) / span) * (HEIGHT - PADDING * 2);
      return `${round(x)},${round(y)}`;
    })
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      className={className}
    >
      <polygon
        points={`0,${HEIGHT} ${line} ${WIDTH},${HEIGHT}`}
        fill="currentColor"
        fillOpacity="0.1"
        stroke="none"
      />
      <polyline
        data-testid="sparkline-line"
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
